/**
 * Shared wallet material for the staging stopgap — the deployment's wallet.
 *
 * What this deployment HAS to hand over: the wallet file it serves, the password
 * it publishes, and where a wallet is imported. The app does not use these to
 * render anything — it passes all three to the SDK through the ONE `configure()`
 * bootstrap (`shared/utils/ngSession`), which owns what a user sees while signing
 * in. This module is the single consumer-facing copy of that material.
 *
 * The correct primitive is the **wallet FILE** (.ngw), NOT a TextCode: a
 * TextCode is a transient device-to-device transfer (5 min, source device
 * online, single use) — useless to embed. A wallet file is STATIC and reusable.
 *
 * ZERO-SECURITY shared credential (friendly users) → embedding the file +
 * password is consistent with the posture.
 *
 * `build.ts` copies the file (from FESTIPOD_SHARED_WALLET_FILE) to the bundle as
 * `/shared-wallet.ngw`, and `define`s the password global from
 * FESTIPOD_SHARED_WALLET_PASSWORD. Empty password → no shared wallet configured
 * → nothing is passed to `configure`.
 */

// Build-injected global (not `process.env`, absent in the browser); any path
// that doesn't inject it reads `undefined` → '' safely (no ReferenceError).
declare global {
  // eslint-disable-next-line no-var
  var __FESTIPOD_SHARED_WALLET_PASSWORD__: string | undefined;
}

export const SHARED_WALLET_PASSWORD: string = globalThis.__FESTIPOD_SHARED_WALLET_PASSWORD__ ?? '';

/** URL of the shared wallet file in the bundle (copied by build.ts). */
export const SHARED_WALLET_FILE_URL = '/shared-wallet.ngw';

/** Standalone NextGraph wallet app — where the import actually happens. */
export const WALLET_IMPORT_URL = 'https://nextgraph.eu/#/wallet/login';

/** Whether this deployment has a shared wallet to hand the SDK. */
export const hasSharedWallet = (): boolean => SHARED_WALLET_PASSWORD.trim().length > 0;
